import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFoodSafetyContext, FS_SCREENS } from '../context/FoodSafetyContext';
import { foodSafetyService } from '../services/foodSafetyService';
import { useMsal } from "@azure/msal-react";
import { SP_MSAL_SCOPE } from '../../../../constants/sharepoint';
// import { alcService } from '../../ALC/services/alcService';
import moment from 'moment';

const ChecklistInformation: React.FC = () => {
    const { state, dispatch } = useFoodSafetyContext();
    const { instance, accounts } = useMsal();
    const navigate = useNavigate();

    const [checklistType, setChecklistType] = useState(state.selectedChecklistType || 'PPE Checklist');
    const [dateStr, setDateStr] = useState(moment().format('DD/MM/YYYY'));
    const [timeStr, setTimeStr] = useState(moment().format('HH:mm:ss'));
    const [rawStartDate, setRawStartDate] = useState(new Date().toISOString());

    const [site, setSite] = useState('Rajpura');
    const [line, setLine] = useState('Line 1');
    const [qaExecutive, setQaExecutive] = useState('');
    const [productionIncharge, setProductionIncharge] = useState('');
    const [areaIncharge, setAreaIncharge] = useState('');
    const [pciArea, setPciArea] = useState('Old Block');
    const [cycle, setCycle] = useState('Cycle-1');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [rawConfigs, setRawConfigs] = useState<any[]>([]);
    const [qaOptions, setQaOptions] = useState<{ label: string; value: string }[]>([]);
    const [prodOptions, setProdOptions] = useState<{ label: string; value: string }[]>([]);

    useEffect(() => {
        const now = new Date();
        setDateStr(moment(now).format('DD/MM/YYYY'));
        setTimeStr(moment(now).format('HH:mm:ss'));
        setRawStartDate(now.toISOString());

        loadPersonnelConfig();
    }, []);

    useEffect(() => {
        if (rawConfigs.length > 0) {
            filterPersonnel(rawConfigs, site, checklistType);
        }
    }, [site, checklistType, rawConfigs]);

    const filterPersonnel = (configs: any[], currentSite: string, currentType: string) => {
        let mappedType = "PPE";
        if (currentType === "GMP Checklist" || currentType === "GMP") mappedType = "GMP";
        else if (currentType === "PCI Checklist" || currentType === "PCI") mappedType = "PCI";

        const matching = configs.filter(c => {
            const matchPlant = (c.Plant || "").toLowerCase() === currentSite.toLowerCase();
            const matchType = (c.ChecklistType || "").toLowerCase() === mappedType.toLowerCase();
            return matchPlant && matchType;
        });

        const qas = new Set<string>();
        const prods = new Set<string>();

        matching.forEach(item => {
            if (item.QAExecutives && Array.isArray(item.QAExecutives)) {
                item.QAExecutives.forEach((u: any) => { if (u.Title) qas.add(u.Title); });
            }
            if (item.ProductionIncharges && Array.isArray(item.ProductionIncharges)) {
                item.ProductionIncharges.forEach((u: any) => { if (u.Title) prods.add(u.Title); });
            }
        });

        // If no strict match, fallback to all users in the plant configs
        if (qas.size === 0 || prods.size === 0) {
            configs.forEach(item => {
                if (item.QAExecutives && Array.isArray(item.QAExecutives)) {
                    item.QAExecutives.forEach((u: any) => { if (u.Title) qas.add(u.Title); });
                }
                if (item.ProductionIncharges && Array.isArray(item.ProductionIncharges)) {
                    item.ProductionIncharges.forEach((u: any) => { if (u.Title) prods.add(u.Title); });
                }
            });
        }

        const qaList = Array.from(qas).map(name => ({ label: name, value: name }));
        const prodList = Array.from(prods).map(name => ({ label: name, value: name }));

        setQaOptions(qaList);
        setProdOptions(prodList);

        const currentLoggedInName = accounts && accounts[0]?.name ? accounts[0].name : "";
        const matchingQa = qaList.find(o => currentLoggedInName && o.value.toLowerCase().includes(currentLoggedInName.toLowerCase()));

        setQaExecutive(prev => {
            if (prev && qaList.some(o => o.value === prev)) return prev;
            if (matchingQa) return matchingQa.value;
            return qaList.length > 0 ? qaList[0].value : '';
        });

        setProductionIncharge(prev => {
            if (prev && prodList.some(o => o.value === prev)) return prev;
            return prodList.length > 0 ? prodList[0].value : '';
        });
    };

    const loadPersonnelConfig = async () => {
        try {
            let spToken = "";
            if (navigator.onLine) {
                try {
                    const tokenResponse = await instance.acquireTokenSilent({ scopes: [SP_MSAL_SCOPE], account: accounts[0] });
                    spToken = tokenResponse.accessToken;
                } catch (e) {
                    console.warn("Silent token acquisition failed.");
                }
            }

            const configs = await foodSafetyService.getConfig(spToken);
            setRawConfigs(configs || []);
            filterPersonnel(configs || [], site, checklistType);
        } catch (err) {
            console.warn("Failed to load personnel config", err);
        }
    };

    const linesBySite: Record<string, string[]> = {
        'Rajpura': ['Line 1', 'Line 2', 'Line 3', 'Line 4', 'Line 5', 'Line 6', 'Line 7', 'Line 8'],
        'Phillaur': ['Line 1', 'Line 2', 'Line 3'],
        'Indore': ['Line 1', 'Line 2'],
        'Tahliwal': ['Line 1', 'Line 2']
    };

    const handleSiteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newSite = e.target.value;
        setSite(newSite);
        const availableLines = linesBySite[newSite] || ['Line 1'];
        setLine(availableLines[0]);
    };

    const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newType = e.target.value;
        setChecklistType(newType);
        dispatch({
            type: 'SET_SELECTIONS',
            payload: { type: newType, shift: state.selectedShift || 'Shift 1' }
        });
    };

    const handleCancel = () => {
        navigate('/');
    };

    const handleSubmit = async () => {
        if (!site || !line || !qaExecutive || !productionIncharge) {
            alert("Please fill in all required fields.");
            return;
        }

        setIsSubmitting(true);
        dispatch({ type: 'SET_LOADING', payload: true });

        try {
            const finalAreaIncharge = areaIncharge.trim() === '' ? productionIncharge : areaIncharge;
            const dateCode = moment(rawStartDate).format("DDMMYYYY");
            const checklistCleanType = checklistType.replace(" Checklist", "");
            
            const sessionPayload: any = {
                cr3ea_food_safety_checklisttype: checklistType,
                cr3ea_plantid: String(site),
                cr3ea_lineno: String(line),
                cr3ea_lineid: String(line),
                cr3ea_assigned_qa: qaExecutive,
                cr3ea_shiftexecutiveproduction: productionIncharge,
                cr3ea_observedby: productionIncharge,
                cr3ea_tourby: qaExecutive,
                cr3ea_tourstartdate: rawStartDate,
                cr3ea_status: "In Progress",
                cr3ea_food_safety_cycle: String(cycle),
                cr3ea_shift: state.selectedShift || "Shift 1",
                cr3ea_title: `FoodSafety_${checklistCleanType}_${site}_${line}_${dateCode}`
            };

            if (checklistType === 'PCI Checklist') {
                sessionPayload.cr3ea_food_safety_areaincharge = finalAreaIncharge;
                sessionPayload.cr3ea_food_safety_area = pciArea;
            }

            const result = await foodSafetyService.saveSession(sessionPayload);
            const tourId = result.cr3ea_prod_rajpura_quality_tourid || result.cr3ea_prod_qualitytourid;

            dispatch({
                type: 'SET_SELECTIONS',
                payload: { type: checklistType, shift: state.selectedShift || 'Shift 1' }
            });

            dispatch({ type: 'SET_TOUR_ID', payload: tourId });
            dispatch({
                type: 'SET_SESSION_DATA',
                payload: {
                    site,
                    line,
                    qaExecutive,
                    productionIncharge,
                    areaIncharge: checklistType === 'PCI Checklist' ? finalAreaIncharge : undefined,
                    area: checklistType === 'PCI Checklist' ? pciArea : undefined,
                    cycle,
                    tourStartDate: rawStartDate
                }
            });

            if (checklistType === 'PPE Checklist') {
                dispatch({ type: 'SET_SCREEN', payload: FS_SCREENS.PPE_CHECKLIST });
            } else if (checklistType === 'GMP Checklist') {
                dispatch({ type: 'SET_SCREEN', payload: FS_SCREENS.GMP_CHECKLIST });
            } else if (checklistType === 'PCI Checklist') {
                dispatch({ type: 'SET_SCREEN', payload: FS_SCREENS.PCI_CHECKLIST });
            }

        } catch (error: any) {
            console.error("Error creating checklist session:", error);
            alert("Failed to initialize session. Please check your network and try again.");
        } finally {
            setIsSubmitting(false);
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    };

    const isStartDisabled = isSubmitting || !site || !line || !qaExecutive || !productionIncharge;

    return (
        <div>
            {/* Stepper box matching screenshot */}
            <div style={{ marginBottom: '20px', background: '#f8fafc', border: '1px solid #e2e8f0', padding: '10px 16px', borderRadius: '6px' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', backgroundColor: '#80cbcf', color: '#ffffff', padding: '4px 12px', borderRadius: '4px', fontWeight: 600, fontSize: '13px' }}>
                    1. Request Info
                </span>
            </div>

            {/* Main Form Card exactly matching screenshot */}
            <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.03)' }}>
                <h5 style={{ fontWeight: 700, color: '#0f172a', fontSize: '16px', marginBottom: '20px' }}>
                    Checklist Information
                </h5>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                    
                    {/* Checklist Type (Full Width) */}
                    <div>
                        <label style={{ display: 'block', fontSize: '13px', color: '#475569', fontWeight: 600, marginBottom: '6px' }}>
                            Checklist Type
                        </label>
                        <select
                            className="form-select"
                            value={checklistType}
                            onChange={handleTypeChange}
                            style={{ height: '42px', fontSize: '14px', border: '1px solid #cbd5e1', borderRadius: '6px', backgroundColor: '#ffffff' }}
                        >
                            <option value="PPE Checklist">PPE Checklist</option>
                            <option value="GMP Checklist">GMP Checklist</option>
                            <option value="PCI Checklist">PCI Checklist</option>
                        </select>
                    </div>

                    {/* Date & Time (2 Columns) */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '18px' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '13px', color: '#475569', fontWeight: 600, marginBottom: '6px' }}>
                                Date
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                value={dateStr}
                                readOnly
                                disabled
                                style={{ height: '42px', fontSize: '14px', backgroundColor: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '6px', color: '#1e293b' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '13px', color: '#475569', fontWeight: 600, marginBottom: '6px' }}>
                                Time
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                value={timeStr}
                                readOnly
                                disabled
                                style={{ height: '42px', fontSize: '14px', backgroundColor: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '6px', color: '#1e293b' }}
                            />
                        </div>
                    </div>

                    {/* Manufacturing Site & Line (2 Columns) */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '18px' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '13px', color: '#475569', fontWeight: 600, marginBottom: '6px' }}>
                                Manufacturing Site
                            </label>
                            <select
                                className="form-select"
                                value={site}
                                onChange={handleSiteChange}
                                style={{ height: '42px', fontSize: '14px', border: '1px solid #cbd5e1', borderRadius: '6px' }}
                            >
                                {Object.keys(linesBySite).map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '13px', color: '#475569', fontWeight: 600, marginBottom: '6px' }}>
                                Line
                            </label>
                            <select
                                className="form-select"
                                value={line}
                                onChange={(e) => setLine(e.target.value)}
                                disabled={!site}
                                style={{ height: '42px', fontSize: '14px', border: '1px solid #cbd5e1', borderRadius: '6px' }}
                            >
                                {site && linesBySite[site]?.map(l => <option key={l} value={l}>{l}</option>)}
                            </select>
                        </div>
                    </div>

                    {/* QA Executive & Production Incharge (2 Columns) */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '18px' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '13px', color: '#475569', fontWeight: 600, marginBottom: '6px' }}>
                                QA Executive
                            </label>
                            {qaOptions.length > 0 ? (
                                <select
                                    className="form-select"
                                    value={qaExecutive}
                                    onChange={(e) => setQaExecutive(e.target.value)}
                                    style={{ height: '42px', fontSize: '14px', border: '1px solid #cbd5e1', borderRadius: '6px' }}
                                >
                                    <option value="">Select QA Executive</option>
                                    {qaOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                </select>
                            ) : (
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Select QA Executive"
                                    value={qaExecutive}
                                    onChange={(e) => setQaExecutive(e.target.value)}
                                    style={{ height: '42px', fontSize: '14px', border: '1px solid #cbd5e1', borderRadius: '6px' }}
                                />
                            )}
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '13px', color: '#475569', fontWeight: 600, marginBottom: '6px' }}>
                                Production Incharge
                            </label>
                            {prodOptions.length > 0 ? (
                                <select
                                    className="form-select"
                                    value={productionIncharge}
                                    onChange={(e) => setProductionIncharge(e.target.value)}
                                    style={{ height: '42px', fontSize: '14px', border: '1px solid #cbd5e1', borderRadius: '6px' }}
                                >
                                    <option value="">Select Production Incharge</option>
                                    {prodOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                </select>
                            ) : (
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Select Production Incharge"
                                    value={productionIncharge}
                                    onChange={(e) => setProductionIncharge(e.target.value)}
                                    style={{ height: '42px', fontSize: '14px', border: '1px solid #cbd5e1', borderRadius: '6px' }}
                                />
                            )}
                        </div>
                    </div>

                    {/* Conditional fields for PCI Checklist */}
                    {checklistType === 'PCI Checklist' && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '18px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', color: '#475569', fontWeight: 600, marginBottom: '6px' }}>
                                    Inspection Block/Area
                                </label>
                                <select
                                    className="form-select"
                                    value={pciArea}
                                    onChange={(e) => setPciArea(e.target.value)}
                                    style={{ height: '42px', fontSize: '14px', border: '1px solid #cbd5e1', borderRadius: '6px' }}
                                >
                                    <option value="Old Block">Old Block (37 Locations)</option>
                                    <option value="New Block">New Block (19 Locations)</option>
                                </select>
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', color: '#475569', fontWeight: 600, marginBottom: '6px' }}>
                                    Area Incharge (Optional)
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Leave blank to fallback to Production Incharge"
                                    value={areaIncharge}
                                    onChange={(e) => setAreaIncharge(e.target.value)}
                                    style={{ height: '42px', fontSize: '14px', border: '1px solid #cbd5e1', borderRadius: '6px' }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Cycle (Full Width) */}
                    <div>
                        <label style={{ display: 'block', fontSize: '13px', color: '#475569', fontWeight: 600, marginBottom: '6px' }}>
                            Cycle
                        </label>
                        <select
                            className="form-select"
                            value={cycle}
                            onChange={(e) => setCycle(e.target.value)}
                            style={{ height: '42px', fontSize: '14px', border: '1px solid #cbd5e1', borderRadius: '6px' }}
                        >
                            <option value="Cycle-1">Cycle-1</option>
                            <option value="Cycle-2">Cycle-2</option>
                            <option value="Cycle-3">Cycle-3</option>
                        </select>
                    </div>

                </div>

                {/* Bottom Buttons matching screenshot */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '30px' }}>
                    <button
                        type="button"
                        onClick={handleCancel}
                        disabled={isSubmitting}
                        style={{
                            backgroundColor: '#f8fafc',
                            border: '1px solid #cbd5e1',
                            borderRadius: '6px',
                            color: '#475569',
                            padding: '8px 22px',
                            fontSize: '13px',
                            fontWeight: 500,
                            cursor: 'pointer'
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isStartDisabled}
                        style={{
                            backgroundColor: '#80cbcf',
                            border: 'none',
                            borderRadius: '6px',
                            color: '#ffffff',
                            padding: '8px 24px',
                            fontSize: '13px',
                            fontWeight: 600,
                            cursor: isStartDisabled ? 'not-allowed' : 'pointer',
                            opacity: isStartDisabled ? 0.6 : 1,
                            transition: 'opacity 0.15s ease'
                        }}
                    >
                        {isSubmitting ? 'Starting...' : 'Start Checklist'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ChecklistInformation;
